import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Button, Table, Row, Col, Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { getRecipe, updateRecipe } from '../../../services/RecipesService';
import { getMaterials } from '../../../services/MaterialLogsService';
import RecipeFormModal from './RecipeFormModal';
import RecipePrintableLabelsWorkspace from './RecipePrintableLabelsWorkspace';
import NutriScoreSection from './NutriScoreSection';
import { quantityToGrams, normalizeForDisplay, formatUnitForDisplay } from '../../../utils/unitConversion';
import { getMaterialName } from '../../../utils/materialName';
import {
    kcalToKj,
    calcNutrientsPer100g,
    roundNutrient,
    fmtNut,
} from '../../../utils/nutritionUtils';

const centerStyle = { textAlign: 'center' };

// ─── Label translations ───────────────────────────────────────────────────────

const LABELS = {
    en: {
        title: 'Nutrition Facts',
        perServing: 'Per serving',
        per100g: 'Per 100 g',
        energy: 'Energy',
        fat: 'Fat',
        saturatedFat: 'Saturated fat',
        carbohydrate: 'Carbohydrate',
        sugars: 'of which sugars',
        fiber: 'Fiber',
        protein: 'Protein',
        salt: 'Salt',
    },
    nl: {
        title: 'Voedingswaarden',
        perServing: 'Per portie',
        per100g: 'Per 100 g',
        energy: 'Energie',
        fat: 'Vetten',
        saturatedFat: 'waarvan verzadigd',
        carbohydrate: 'Koolhydraten',
        sugars: 'waarvan suikers',
        fiber: 'Vezels',
        protein: 'Eiwitten',
        salt: 'Zout',
    },
};

/** EU salt declaration: salt (g) = sodium (mg) × 2.5 / 1000, rounded to 2 decimals. */
function formatSaltG(sodiumG, locale) {
    if (sodiumG == null) return '—';
    const saltG = roundNutrient(sodiumG * 2.5, 2);
    if (saltG == null) return '—';
    const str = saltG.toFixed(2);
    return locale === 'nl' ? str.replace('.', ',') : str;
}

// ─── Nutrition Facts Panel ────────────────────────────────────────────────────

function NfRow({ label, perServing, per100g, unit = 'g', decimals = 1, sub = false, strong = false }) {
    const ps = perServing != null ? fmtNut(perServing, decimals) : '—';
    const p1 = per100g   != null ? fmtNut(per100g,   decimals) : '—';
    const labelClass = sub ? 'nf-row-sub' : strong ? 'nf-row-primary' : '';
    return (
        <tr className={labelClass}>
            <td>
                {sub && <span className="nf-sub-bullet">· </span>}
                {label}
            </td>
            <td className="nf-cell-num">{ps} {unit}</td>
            <td className="nf-cell-num">{p1} {unit}</td>
        </tr>
    );
}

const NutritionPanel = React.forwardRef(function NutritionPanel(
    { nutrition, portionWeightG, servingLabel, locale = 'en', hasIncomplete },
    ref
) {
    const L = LABELS[locale] || LABELS.en;
    const perPortion = nutrition?.perServing ?? nutrition?.total ?? null;
    const portionWeight = parseFloat(portionWeightG);
    const hasValidWeight = Number.isFinite(portionWeight) && portionWeight > 0;

    const per100g = useMemo(() => {
        if (!perPortion || !hasValidWeight) return null;
        return calcNutrientsPer100g(perPortion, portionWeight);
    }, [perPortion, portionWeight, hasValidWeight]);

    return (
        <div className="nf-panel" ref={ref}>
            {/* Header */}
            <div className="nf-panel-header">
                <h5 className="nf-title">{L.title}</h5>
                {hasIncomplete && (
                    <small className="nf-incomplete-warning">
                        ⚠ Some ingredients are missing nutrition data
                    </small>
                )}
            </div>

            <div className="nf-divider-thick" />

            {/* Nutrition table */}
            <table className="nf-table" aria-label={`${L.title} per serving and per 100g`}>
                <thead>
                    <tr>
                        <th scope="col"></th>
                        <th scope="col" className="nf-cell-num">
                            {L.perServing}<br /><small>({servingLabel})</small>
                        </th>
                        <th scope="col" className="nf-cell-num">{L.per100g}</th>
                    </tr>
                </thead>
                <tbody>
                    {/* Energy — emphasised */}
                    <tr className="nf-row-energy">
                        <td>{L.energy}</td>
                        <td className="nf-cell-num nf-energy-val">
                            {perPortion ? (
                                <>
                                    <strong>{fmtNut(perPortion.energy, 0)} kcal</strong>
                                    <br />
                                    <span className="nf-kj">{kcalToKj(perPortion.energy) ?? '—'} kJ</span>
                                </>
                            ) : '—'}
                        </td>
                        <td className="nf-cell-num nf-energy-val">
                            {per100g ? (
                                <>
                                    <strong>{fmtNut(per100g.energy, 0)} kcal</strong>
                                    <br />
                                    <span className="nf-kj">{kcalToKj(per100g.energy) ?? '—'} kJ</span>
                                </>
                            ) : '—'}
                        </td>
                    </tr>

                    <tr className="nf-row-divider"><td colSpan={3} /></tr>

                    {/* Fat */}
                    <NfRow label={L.fat}          perServing={perPortion?.fat}          per100g={per100g?.fat}          strong />
                    <NfRow label={L.saturatedFat} perServing={perPortion?.saturatedFat}  per100g={per100g?.saturatedFat}  sub />

                    <tr className="nf-row-divider"><td colSpan={3} /></tr>

                    {/* Carbohydrate */}
                    <NfRow label={L.carbohydrate} perServing={perPortion?.carbohydrate} per100g={per100g?.carbohydrate} strong />
                    <NfRow label={L.sugars}       perServing={perPortion?.sugars}       per100g={per100g?.sugars}       sub />

                    <tr className="nf-row-divider"><td colSpan={3} /></tr>

                    {/* Fiber */}
                    <NfRow label={L.fiber}   perServing={perPortion?.fiber}   per100g={per100g?.fiber}   strong />

                    <tr className="nf-row-divider"><td colSpan={3} /></tr>

                    {/* Protein */}
                    <NfRow label={L.protein} perServing={perPortion?.protein} per100g={per100g?.protein} strong />

                    <tr className="nf-row-divider"><td colSpan={3} /></tr>

                    {/* Salt (stored as sodium g/100g; salt g = sodium mg × 2.5 / 1000) */}
                    <tr className="nf-row-primary">
                        <td>{L.salt}</td>
                        <td className="nf-cell-num">
                            {perPortion?.sodium != null
                                ? `${formatSaltG(perPortion.sodium, locale)} g`
                                : '—'}
                        </td>
                        <td className="nf-cell-num">
                            {per100g?.sodium != null
                                ? `${formatSaltG(per100g.sodium, locale)} g`
                                : '—'}
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    );
});

// ─── Main component ──────────────────────────────────────────────────────────

export default function RecipeDetail() {
    const { i18n } = useTranslation();
    const { id } = useParams();
    const navigate = useNavigate();
    const [recipe, setRecipe] = useState(null);
    const [materials, setMaterials] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showEditModal, setShowEditModal] = useState(false);
    const [costForAmount, setCostForAmount] = useState('1');
    const [portionWeightG, setPortionWeightG] = useState('');
    const savePortionWeightRef = useRef(null);
    const enLabelRef = useRef(null);
    const nlLabelRef = useRef(null);

    useEffect(() => {
        const load = async () => {
            if (!id) return;
            try {
                setLoading(true);
                const [recipeRes, materialsRes] = await Promise.all([
                    getRecipe(id),
                    getMaterials().catch(() => ({ data: [] })),
                ]);
                const r = recipeRes.data;
                setRecipe(r);
                setMaterials(materialsRes.data || []);
                const pw = r?.portion_weight_grams;
                setPortionWeightG(pw != null && pw !== '' ? String(pw) : '');
            } catch {
                toast.error('Recipe not found');
                navigate('/recipes');
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [id, navigate]);

    const savePortionWeight = (value) => {
        if (!recipe?.id) return;
        const num = value === '' || value == null ? null : parseFloat(value);
        if (num !== null && (isNaN(num) || num <= 0)) return;
        updateRecipe(recipe.id, { portion_weight_grams: num })
            .then((res) => {
                setRecipe(res.data);
                const pw = res.data?.portion_weight_grams;
                setPortionWeightG(pw != null ? String(pw) : '');
                toast.success('Serving size saved');
            })
            .catch(() => toast.error('Failed to save serving size'));
    };

    const handlePortionWeightChange = (e) => {
        const v = e.target.value;
        setPortionWeightG(v);
        if (savePortionWeightRef.current) clearTimeout(savePortionWeightRef.current);
        savePortionWeightRef.current = setTimeout(() => {
            const num = v === '' ? null : parseFloat(v);
            if (v === '' || (Number.isFinite(num) && num > 0)) savePortionWeight(v === '' ? '' : num);
        }, 600);
    };

    const downloadLabelAsPng = useCallback(async (ref, recipeName, locale) => {
        if (!ref.current) return;
        try {
            const { default: html2canvas } = await import('html2canvas');
            const canvas = await html2canvas(ref.current, {
                scale: 2,
                backgroundColor: '#ffffff',
                logging: false,
            });
            const a = document.createElement('a');
            a.download = `${(recipeName || 'nutrition').replace(/[^\w-]/g, '_')}_${locale}.png`;
            a.href = canvas.toDataURL('image/png');
            a.click();
        } catch {
            toast.error('Failed to download label');
        }
    }, []);

    const totalExcl = recipe?.items?.reduce((s, it) => s + (parseFloat(it.cost_excl_tax) || 0), 0) ?? 0;
    const totalIncl = recipe?.items?.reduce((s, it) => s + (parseFloat(it.cost_incl_tax ?? it.cost) || 0), 0) ?? 0;

    // Consolidate duplicate materials
    const consolidatedItems = (recipe?.items || []).reduce((acc, it) => {
        const key = (it.material_name || '').toLowerCase().trim() || it.material_id;
        const existing = acc.find((x) => ((x.material_name || '').toLowerCase().trim() || x.material_id) === key);
        const qty = parseFloat(it.quantity) || 0;
        const costExcl = parseFloat(it.cost_excl_tax) || 0;
        const costIncl = parseFloat(it.cost_incl_tax ?? it.cost) || 0;
        if (existing) {
            existing.quantity += qty;
            existing.cost_excl_tax = (parseFloat(existing.cost_excl_tax) || 0) + costExcl;
            existing.cost_incl_tax = (parseFloat(existing.cost_incl_tax ?? existing.cost) || 0) + costIncl;
        } else {
            acc.push({ ...it, quantity: qty, cost_excl_tax: costExcl, cost_incl_tax: costIncl });
        }
        return acc;
    }, []);

    const displayItems = useMemo(() => consolidatedItems.map((it) => {
        const unit = it.unit || it.material_unit || 'unit';
        const { quantity: displayQty, unit: displayUnit } = normalizeForDisplay(it.quantity, unit, { massDisplay: 'g', volumeDisplay: 'ml' });
        return { ...it, displayQuantity: displayQty, displayUnit };
    }), [consolidatedItems, i18n.resolvedLanguage]);

    const yieldAmt = parseFloat(recipe?.yield_amount) || 1;
    const costForAmt = parseFloat(costForAmount) || 0;
    const ratio = yieldAmt > 0 && costForAmt >= 0 ? costForAmt / yieldAmt : 0;
    const costForExcl = Math.round(totalExcl * ratio * 100) / 100;
    const costForIncl = Math.round(totalIncl * ratio * 100) / 100;

    /**
     * Aggregate recipe nutrition from ingredients.
     * Per-100g is computed in NutritionPanel from perServing + portionWeight.
     */
    const nutrition = useMemo(() => {
        if (!recipe?.items?.length || !materials.length) return null;
        const matById = {};
        for (const m of materials) matById[m.id] = m;

        let energy = 0, protein = 0, fat = 0, saturatedFat = 0, transFat = 0;
        let carb = 0, sugars = 0, fiber = 0, sodium = 0;
        let hasSaturatedFat = false, hasTransFat = false, hasSugars = false, hasSodium = false;
        let hasIncomplete = false;

        for (const it of recipe.items) {
            const mat = matById[it.material_id];
            if (!mat || mat.is_non_food) continue;
            const e = mat.energy_per_100g, p = mat.protein_per_100g;
            const f = mat.fat_per_100g, c = mat.carbohydrate_per_100g;
            if (e == null || p == null || f == null || c == null) {
                hasIncomplete = true;
                continue;
            }
            const grams = quantityToGrams(it.quantity, it.material_unit || it.unit || mat.unit);
            const factor = grams / 100;
            energy += factor * e;
            protein += factor * p;
            fat += factor * f;
            carb += factor * c;
            fiber += factor * (mat.fiber_per_100g || 0);

            if (mat.saturated_fat_per_100g != null) { saturatedFat += factor * mat.saturated_fat_per_100g; hasSaturatedFat = true; }
            if (mat.trans_fat_per_100g     != null) { transFat     += factor * mat.trans_fat_per_100g;     hasTransFat = true; }
            if (mat.sugars_per_100g        != null) { sugars       += factor * mat.sugars_per_100g;        hasSugars = true; }
            if (mat.sodium_per_100g        != null) { sodium       += factor * mat.sodium_per_100g;        hasSodium = true; }
        }

        const r1 = (v) => roundNutrient(v, 1);
        const r0 = (v) => roundNutrient(v, 0);

        const total = {
            energy:       r0(energy),
            protein:      r1(protein),
            fat:          r1(fat),
            saturatedFat: hasSaturatedFat ? r1(saturatedFat) : null,
            transFat:     hasTransFat     ? r1(transFat)     : null,
            carbohydrate: r1(carb),
            sugars:       hasSugars       ? r1(sugars)       : null,
            fiber:        r1(fiber),
            sodium:       hasSodium       ? roundNutrient(sodium, 4) : null,
        };

        const perServing = yieldAmt > 1
            ? {
                energy:       r0(energy / yieldAmt),
                protein:      r1(protein / yieldAmt),
                fat:          r1(fat / yieldAmt),
                saturatedFat: hasSaturatedFat ? r1(saturatedFat / yieldAmt) : null,
                transFat:     hasTransFat     ? r1(transFat / yieldAmt)     : null,
                carbohydrate: r1(carb / yieldAmt),
                sugars:       hasSugars       ? r1(sugars / yieldAmt)       : null,
                fiber:        r1(fiber / yieldAmt),
                sodium:       hasSodium       ? roundNutrient(sodium / yieldAmt, 4) : null,
            }
            : null;

        return { total, perServing, hasIncomplete };
    }, [recipe, materials, yieldAmt]);

    // ─────────────────────────────────────────────────────────────────────────

    if (loading) {
        return (
            <div className="container-fluid">
                <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
            </div>
        );
    }

    if (!recipe) return null;

    const yieldUnit = (recipe.yield_unit && !['1', 'unit'].includes(String(recipe.yield_unit).toLowerCase()))
        ? recipe.yield_unit
        : 'portion';

    const servingLabel = yieldAmt > 1
        ? `1 ${yieldUnit}`
        : `${yieldAmt} ${yieldUnit}`;

    return (
        <div className="container-fluid">
            <div className="row">
                <div className="col-12">
                    <Card>
                        <Card.Header className="d-flex justify-content-between align-items-center flex-wrap">
                            <div>
                                <Button variant="light" size="sm" className="mb-2" onClick={() => navigate('/recipes')}>
                                    <i className="fa-solid fa-arrow-left me-1" /> Back to Recipes
                                </Button>
                                <h4 className="heading mb-0">{recipe.name}</h4>
                                <span className="text-muted">Makes: {recipe.yield_amount} {yieldUnit}</span>
                            </div>
                            <Button variant="primary" onClick={() => setShowEditModal(true)}>
                                <i className="fa-solid fa-pen me-1" /> Edit
                            </Button>
                        </Card.Header>
                        <Card.Body>
                            {/* Ingredients table */}
                            <div className="table-responsive mb-4">
                                <Table bordered className="recipe-detail-centered recipe-detail-ingredients">
                                    <thead>
                                        <tr>
                                            <th style={centerStyle}>Material</th>
                                            <th style={centerStyle}>Quantity</th>
                                            <th style={centerStyle}>Unit</th>
                                            <th style={centerStyle}>Price (exc)</th>
                                            <th style={centerStyle}>Price (inc)</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {displayItems.map((it, idx) => (
                                            <tr key={it.id || idx}>
                                                <td style={centerStyle}>{getMaterialName(it)}</td>
                                                <td style={centerStyle}>{typeof it.displayQuantity === 'number' ? it.displayQuantity.toFixed(2) : parseFloat(it.quantity).toFixed(2)}</td>
                                                <td style={centerStyle}>{formatUnitForDisplay(it.displayUnit ?? it.unit ?? it.material_unit)}</td>
                                                <td style={centerStyle}>{(parseFloat(it.cost_excl_tax) || 0).toFixed(2)}</td>
                                                <td style={centerStyle}>{(parseFloat(it.cost_incl_tax ?? it.cost) || 0).toFixed(2)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>

                            {/* Cost summary */}
                            <div className="p-4 bg-light rounded text-center">
                                <Row className="justify-content-center">
                                    <Col md={6} className="text-center">
                                        <h6 className="text-muted mb-2">Total ({recipe.yield_amount} {yieldUnit})</h6>
                                        <p className="mb-0 fs-5">
                                            <strong>Price (exc):</strong> {totalExcl.toFixed(2)} &nbsp;
                                            <strong>Price (inc):</strong> {totalIncl.toFixed(2)}
                                        </p>
                                    </Col>
                                    <Col md={6} className="text-center">
                                        <h6 className="text-muted mb-2">Cost for your portion</h6>
                                        <Row className="align-items-center justify-content-center">
                                            <Col xs={4}>
                                                <Form.Control
                                                    type="number"
                                                    step="0.01"
                                                    min="0"
                                                    placeholder="e.g. 1"
                                                    value={costForAmount}
                                                    onChange={(e) => setCostForAmount(e.target.value)}
                                                />
                                            </Col>
                                            <Col xs={2} className="text-muted text-center">{yieldUnit}</Col>
                                            <Col xs={6} className="text-center">
                                                <p className="mb-0 fw-bold">
                                                    (exc) {costForExcl.toFixed(2)} / (inc) {costForIncl.toFixed(2)}
                                                </p>
                                            </Col>
                                        </Row>
                                    </Col>
                                </Row>
                            </div>

                            {/* Nutrition labels */}
                            {nutrition && (
                                <div className="mt-4">
                                    {/* Serving size input — outside the label boxes */}
                                    <div className="nf-serving-row mb-3">
                                        <Form.Group className="mb-0 d-flex align-items-center gap-2">
                                            <Form.Label className="mb-0 nf-meta-label" htmlFor="nf-serving-input">
                                                Serving size
                                            </Form.Label>
                                            <Form.Control
                                                id="nf-serving-input"
                                                type="number"
                                                min="1"
                                                step="1"
                                                placeholder="e.g. 400"
                                                value={portionWeightG}
                                                onChange={handlePortionWeightChange}
                                                aria-label="Serving size in grams"
                                                className="nf-serving-input"
                                            />
                                            <span className="nf-meta-label">g</span>
                                            {!portionWeightG && (
                                                <small className="text-muted">
                                                    — enter weight for per-100 g values
                                                </small>
                                            )}
                                        </Form.Group>
                                    </div>

                                    <div className="recipe-nutrition-workspace">
                                        <div className="recipe-nutrition-workspace__facts">
                                            <div className="nf-labels-row">
                                                {/* English label */}
                                                <div className="nf-label-wrapper">
                                                    <NutritionPanel
                                                        ref={enLabelRef}
                                                        nutrition={nutrition}
                                                        portionWeightG={portionWeightG}
                                                        servingLabel={servingLabel}
                                                        locale="en"
                                                        hasIncomplete={nutrition.hasIncomplete}
                                                    />
                                                    <Button
                                                        variant="outline-secondary"
                                                        size="sm"
                                                        className="mt-2 nf-download-btn"
                                                        onClick={() => downloadLabelAsPng(enLabelRef, recipe.name, 'en')}
                                                    >
                                                        <i className="fa-solid fa-download me-1" /> Download EN
                                                    </Button>
                                                </div>

                                                {/* Dutch label */}
                                                <div className="nf-label-wrapper">
                                                    <NutritionPanel
                                                        ref={nlLabelRef}
                                                        nutrition={nutrition}
                                                        portionWeightG={portionWeightG}
                                                        servingLabel={servingLabel}
                                                        locale="nl"
                                                        hasIncomplete={nutrition.hasIncomplete}
                                                    />
                                                    <Button
                                                        variant="outline-secondary"
                                                        size="sm"
                                                        className="mt-2 nf-download-btn"
                                                        onClick={() => downloadLabelAsPng(nlLabelRef, recipe.name, 'nl')}
                                                    >
                                                        <i className="fa-solid fa-download me-1" /> Download NL
                                                    </Button>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="recipe-nutrition-workspace__labels">
                                            <NutriScoreSection
                                                recipe={recipe}
                                                nutrition={nutrition}
                                                portionWeightG={portionWeightG}
                                                onRecipeUpdated={(result) => {
                                                    setRecipe((prev) => ({
                                                        ...prev,
                                                        ...(result.letter != null && { nutriscore_letter: result.letter }),
                                                        ...(result.score != null && { nutriscore_score: result.score }),
                                                        ...(result.fvl_percent != null && { nutriscore_fvl_percent: result.fvl_percent }),
                                                        ...(result.nutriscore_show_on_label != null && { nutriscore_show_on_label: result.nutriscore_show_on_label }),
                                                    }));
                                                }}
                                            />
                                            <RecipePrintableLabelsWorkspace
                                                recipe={recipe}
                                                materials={materials}
                                                nutrition={nutrition}
                                                servingLabel={servingLabel}
                                                portionWeightG={portionWeightG}
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </div>
            </div>

            <RecipeFormModal
                show={showEditModal}
                onHide={() => setShowEditModal(false)}
                recipeId={recipe.id}
                onSaved={() => {
                    getRecipe(recipe.id).then((res) => setRecipe(res.data));
                    setShowEditModal(false);
                }}
            />
        </div>
    );
}
